import React, { type ReactNode } from 'react';
import noop from 'lodash/noop';
import type { UIAnalyticsEvent } from '@atlaskit/analytics-next';
import Button from '@atlaskit/button/standard-button';
// eslint-disable-next-line @typescript-eslint/no-restricted-imports
import ModalDialog, {
	ModalTransition,
	ModalBody,
	ModalTitle,
	ModalHeader,
	ModalFooter,
} from '@atlaskit/modal-dialog';
import ShortcutScope from '@atlassian/jira-common-components-keyboard-shortcuts/src/shortcut-scope.tsx';
import { useIntl } from '@atlassian/jira-intl';
import { fireUIAnalytics } from '@atlassian/jira-product-analytics-bridge';
import { REMOVE_GROUP_GUARDS } from '../../../common/constants';
import type { RemoveConfirmation, TicketType } from '../../../common/types';
import messages from './messages';

const getOrphanMessage = (message: string, ticketTypes: TicketType[]) => (
	<div>
		{message}
		<br />
		<ul>
			{ticketTypes.map<ReactNode>(({ id, name }: TicketType) => (
				<li key={id}>{name}</li>
			))}
		</ul>
	</div>
);

const RemoveGroupModal = ({
	details: { reason, affectedTicketTypes },
	onClose,
	onConfirm,
}: RemoveConfirmation) => {
	const { formatMessage } = useIntl();

	const heading = formatMessage(
		reason === REMOVE_GROUP_GUARDS.oneTicketTypeLeft
			? messages.removeTicketTypeHeading
			: messages.removeGroupHeading,
	);

	let body: string | JSX.Element = formatMessage(messages.confirmMessage);

	if (reason === REMOVE_GROUP_GUARDS.oneTicketTypeLeft) {
		body = formatMessage(messages.emptyGroupMessage);
	} else if (reason === REMOVE_GROUP_GUARDS.orphanTicketTypes && affectedTicketTypes) {
		body = getOrphanMessage(
			formatMessage(messages.orphanRequestForms, { count: affectedTicketTypes.length }),
			affectedTicketTypes,
		);
	}
	return (
		<ModalTransition>
			<ShortcutScope>
				<ModalDialog shouldCloseOnOverlayClick shouldCloseOnEscapePress>
					<ModalHeader>
						<ModalTitle appearance="danger">{heading}</ModalTitle>
					</ModalHeader>

					<ModalBody>{body}</ModalBody>
					<ModalFooter>
						<Button
							appearance="subtle"
							onClick={(_, analyticEvent: UIAnalyticsEvent) => {
								fireUIAnalytics(analyticEvent, 'deleteGroupDialogClose');
								onClose();
							}}
						>
							{formatMessage(messages.cancelButton)}
						</Button>
						<Button
							appearance="danger"
							onClick={(_, analyticEvent: UIAnalyticsEvent) => {
								fireUIAnalytics(analyticEvent, 'deleteGroupDialogConfirm');
								onConfirm();
							}}
							testId="servicedesk-portal-settings-request-groups.ui.portal-group.remove-group-modal.confirm-button"
						>
							{formatMessage(messages.confirmButton)}
						</Button>
					</ModalFooter>
				</ModalDialog>
			</ShortcutScope>
		</ModalTransition>
	);
};

RemoveGroupModal.defaultProps = {
	onClose: noop,
	onConfirm: noop,
};

export default RemoveGroupModal;
